import { useEffect, useState } from 'react';

/**
 * Countdown timer with localStorage persistence.
 * - Each visitor gets their own 4-hour countdown that survives refreshes.
 * - When the timer hits zero, it automatically restarts for another full cycle
 *   to keep the promotional urgency alive.
 */
const STORAGE_KEY = 'arete_promo_deadline_v1';
const DURATION_MS = 4 * 60 * 60 * 1000; // 4 hours

const getOrCreateDeadline = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const now = Date.now();
    if (stored) {
      const parsed = parseInt(stored, 10);
      if (!Number.isNaN(parsed) && parsed > now) {
        return parsed;
      }
    }
    const next = now + DURATION_MS;
    localStorage.setItem(STORAGE_KEY, String(next));
    return next;
  } catch (e) {
    return Date.now() + DURATION_MS;
  }
};

const pad = (n) => String(n).padStart(2, '0');

export const CountdownTimer = ({ variant = 'dark', compact = false }) => {
  const [deadline, setDeadline] = useState(() => getOrCreateDeadline());
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const interval = setInterval(() => {
      const current = Date.now();
      if (current >= deadline) {
        // Auto-restart cycle
        const next = current + DURATION_MS;
        try { localStorage.setItem(STORAGE_KEY, String(next)); } catch (_e) { /* ignore */ }
        setDeadline(next);
        setNow(current);
      } else {
        setNow(current);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [deadline]);

  const remaining = Math.max(0, deadline - now);
  const hours = Math.floor(remaining / (1000 * 60 * 60));
  const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((remaining % (1000 * 60)) / 1000);

  const isDark = variant === 'dark';
  const wrapperClass = isDark
    ? 'bg-gradient-to-r from-rose-600 via-red-600 to-orange-600 text-white'
    : 'bg-rose-50 text-rose-700 border border-rose-200';

  if (compact) {
    return (
      <div
        data-testid="promo-countdown-compact"
        className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${wrapperClass}`}
      >
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-white/90 animate-pulse" />
        <span>Oferta termina em</span>
        <span className="font-mono tabular-nums tracking-tight">
          {pad(hours)}:{pad(minutes)}:{pad(seconds)}
        </span>
      </div>
    );
  }

  return (
    <div
      data-testid="promo-countdown"
      className={`flex flex-col items-center justify-center gap-2 rounded-2xl px-5 py-4 ${wrapperClass}`}
    >
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest">
        <span className="inline-block w-2 h-2 rounded-full bg-white/90 animate-pulse" />
        Oferta termina em
      </div>
      <div className="flex items-center gap-2 font-mono text-2xl md:text-3xl font-bold tabular-nums">
        <span data-testid="countdown-hours" className="bg-black/20 rounded-lg px-3 py-1">{pad(hours)}</span>
        <span className="opacity-70">:</span>
        <span data-testid="countdown-minutes" className="bg-black/20 rounded-lg px-3 py-1">{pad(minutes)}</span>
        <span className="opacity-70">:</span>
        <span data-testid="countdown-seconds" className="bg-black/20 rounded-lg px-3 py-1">{pad(seconds)}</span>
      </div>
      <div className="flex items-center gap-3 text-[10px] uppercase tracking-wider opacity-90">
        <span>Horas</span>
        <span>Minutos</span>
        <span>Segundos</span>
      </div>
    </div>
  );
};

export default CountdownTimer;
